import { ReactNode, createContext, useContext, useMemo, useState } from "react";
import { Locale, TranslationKey, dictionaries, locales } from "@/i18n/translations";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: (key: TranslationKey) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLocale(): Locale {
  const stored = window.localStorage.getItem("verdantfall.locale");
  if (stored === "en" || stored === "es") {
    return stored;
  }

  const browserLocale = window.navigator.language.toLowerCase();
  return browserLocale.startsWith("es") ? "es" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const value = useMemo<I18nContextValue>(() => {
    function setLocale(nextLocale: Locale) {
      if (!locales.includes(nextLocale)) {
        return;
      }
      window.localStorage.setItem("verdantfall.locale", nextLocale);
      document.documentElement.lang = nextLocale;
      setLocaleState(nextLocale);
    }

    function toggleLocale() {
      setLocale(locale === "en" ? "es" : "en");
    }

    function t(key: TranslationKey) {
      return dictionaries[locale][key] ?? dictionaries.en[key] ?? key;
    }

    return { locale, setLocale, toggleLocale, t };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
}
